/**
 * @file: useImagePreview.ts
 *
 * The full-screen image-preview overlay — one responsibility. Tapping an
 * image bubble opens the viewer on ALL images in the conversation (so the
 * user can swipe between them), positioned at the tapped one; the viewer
 * closes it. State is a subject so only the overlay re-renders.
 */

import { useCreation } from '@euler/utils/hooks';
import { useCallback, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

/** The open viewer: every previewable image + which one is showing. */
export interface ImagePreviewState {
  images: string[];
  index: number;
}

export interface ImagePreview {
  /** The open preview, or null when closed. */
  state$: BehaviorSubject<ImagePreviewState | null>;
  open: (images: string[], index: number) => void;
  close: () => void;
}

export function useImagePreview(): ImagePreview {
  const state$ = useCreation(
    () => new BehaviorSubject<ImagePreviewState | null>(null),
    [],
  );
  const open = useCallback(
    (images: string[], index: number) => state$.next({ images, index }),
    [state$],
  );
  const close = useCallback(() => state$.next(null), [state$]);
  return useMemo(() => ({ state$, open, close }), [state$, open, close]);
}
